import { useState } from "react";
import { Link } from "react-router-dom";
import {
	FiArrowRight,
	FiBookOpen,
	FiCheckCircle,
	FiCreditCard,
	FiInfo,
	FiPlus,
	FiUsers,
} from "react-icons/fi";
import Moment from "react-moment";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import useFreeBooksQuota from "api/BookResource/useFreeBooksQuota";
import useProfile from "api/UserResource/useProfile";
import useAuthStore from "store/authStore";
import { Role } from "utils/server-constants/roles";
import AddSeatsModal from "components/AddSeatsModal";
import AccountPage from "../AccountPage";
import AccountLayout from "../AccountLayout";

const MySubscription = () => {
	const [seatsOpen, setSeatsOpen] = useState(false);
	const hasRoles = useAuthStore((state) => state.hasRoles);
	const {
		data: result,
		isLoading: loadingSubscription,
		isError,
		refetch,
	} = useCheckIfUserIsSubscribed();
	const { data: quota } = useFreeBooksQuota();
	const { data: profile, isLoading } = useProfile();
	const plan = result?.data;
	const institutional = plan?.category === "institution";
	const canAddSeats = institutional && hasRoles([Role.Institution, Role.Educator]);
	const seats = plan?.seats_purchased || plan?.number_of_seats || 1;
	const allocations = plan?.max_allocations_per_seat || plan?.max_number_of_books || 0;
	const picks = plan?.picks_count ?? plan?.current_premium_books_added ?? 0;
	const policy = plan?.picks_policy || (institutional ? "unlimited" : "limited");
	const remaining = plan?.remaining_picks ?? plan?.remaining ?? 0;
	const metrics = institutional
		? [
				{ label: "Seats purchased", value: seats, icon: FiUsers },
				{ label: "Allocations per seat", value: allocations, icon: FiBookOpen },
				{
					label: "Picks bank",
					value: policy === "unlimited" ? "Unlimited" : remaining,
					icon: FiCreditCard,
				},
			]
		: [
				{ label: "Total premium picks", value: plan?.max_number_of_books ?? 0, icon: FiBookOpen },
				{ label: "Picks used", value: picks, icon: FiCheckCircle },
				{ label: "Picks remaining", value: remaining, icon: FiCreditCard },
			];
	return (
		<AccountPage>
			<AccountLayout isLoading={isLoading || loadingSubscription} profile={profile?.data}>
				{isError ? (
					<div className="account-empty" role="alert">
						<FiCreditCard aria-hidden="true" />
						<h2>We couldn’t load your subscription</h2>
						<p>Please try again to see your plan.</p>
						<button type="button" className="account-button" onClick={() => refetch()}>
							Try again
						</button>
					</div>
				) : !plan ? (
					<div className="account-empty">
						<FiBookOpen aria-hidden="true" />
						<h2>More stories are waiting</h2>
						<p>You don’t have an active subscription. Find a plan that fits the way you read.</p>
						{quota?.data?.unlocked_unlimited ? (
							<p>Free books are unlimited for your account.</p>
						) : (
							quota?.data && (
								<p>
									You can add up to {quota.data.max_free_books} free books without a subscription,
									with {quota.data.remaining_free_books} remaining. Purchase or subscribe to unlock
									unlimited free books.
								</p>
							)
						)}
						<Link to="/subscriptions" className="account-button">
							Explore plans <FiArrowRight aria-hidden="true" />
						</Link>
					</div>
				) : (
					<>
						<div className="account-profile-summary">
							<div>
								<span className="account-eyebrow">Your current plan</span>
								<h2 className="account-plan-name">{plan.name}</h2>
								{plan.expiry_date && (
									<p className="account-helper">
										Expires <Moment format="D MMM YYYY">{plan.expiry_date}</Moment>
									</p>
								)}
							</div>
							<span className="account-badge">
								<FiCheckCircle aria-hidden="true" />
								Active subscription
							</span>
						</div>
						<div className="account-section-heading">
							<h2>{institutional ? "Your institution’s capacity" : "Room for your next read"}</h2>
							<p>
								{institutional
									? `${picks} ${picks === 1 ? "book" : "books"} currently in your picks bank.`
									: "Your premium reading picks for this plan."}
							</p>
						</div>
						<div className="account-stats-grid">
							{metrics.map(({ label, value, icon: Icon }) => (
								<div key={label} className="account-stat">
									<Icon aria-hidden="true" />
									<p className="account-stat-value">{value}</p>
									<p className="account-stat-label">{label}</p>
								</div>
							))}
						</div>
						<div className="account-note mt-6">
							<FiInfo aria-hidden="true" />
							<div>
								<strong>
									{institutional ? "How institution access works" : "A note on reading picks"}
								</strong>
								{institutional ? (
									`Each student can receive up to ${allocations} books. Each picked book can be allocated to up to ${seats} unique students before you need to add seats.`
								) : (
									<>
										Premium picks give you access to paid books in the subscription catalog. Free
										books are unlimited and don’t count towards your premium pick limit.
										{plan.current_free_books_added > 0 &&
											` You currently have ${plan.current_free_books_added} free books in your library.`}
									</>
								)}
							</div>
						</div>
						<div className="account-form-actions">
							<Link to="/subscriptions" className="account-inline-link">
								Compare subscription plans <FiArrowRight aria-hidden="true" />
							</Link>
							<div className="account-form-buttons">
								{canAddSeats && (
									<button
										type="button"
										className="account-button secondary"
										onClick={() => setSeatsOpen(true)}
									>
										<FiPlus aria-hidden="true" />
										Add seats
									</button>
								)}
								<Link to="/my-library" className="account-button">
									Go to my library <FiArrowRight aria-hidden="true" />
								</Link>
							</div>
						</div>
					</>
				)}
				{canAddSeats && (
					<AddSeatsModal
						visible={seatsOpen}
						onHide={() => setSeatsOpen(false)}
						subscription={plan}
					/>
				)}
			</AccountLayout>
		</AccountPage>
	);
};
export default MySubscription;
